/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import CloseButton from '@ion/dls/src/components/buttons/CloseButton';
import SearchField from '@ion/dls/src/components/search/SearchField';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import TopNavSearchIcon from '@ion/dls/src/icons/TopNavSearchIcon';
import Render from '@ion/dls/src/render/Render';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';
import withSignals from '../../components/hoc/withSignals';
import ReportCard from './ReportCard';
import ReportsFilter from './ReportsFilter';
import {searchValue, setSearchValue} from './store';
import {Headertabs, REPORT_CARD_WIDTH, SideNavTabs} from './types';
import {getCurrentDashboards, getReportFilterOptions} from './utils';

/**
 * Props for the HomeView component.
 */
interface HomeViewProps {
  toggleCategory: (categoryName: string, value?: boolean) => void;
  categories: GenericObject[];
  allCategories: GenericObject[];
  setSelected: (dashboard: any) => void;
  selectedCategory: string | null;
  sortByDate?: boolean;
  setSideTab: Function;
  setCategoryFilter: (categories: string[], headerTab?: Headertabs, sideTab?: SideNavTabs) => void;
  categoryFilter: string[];
  omitReportFilter?: boolean;
};

const useStyles = makeStyles((theme) => ({
  root : {
    display                        : 'flex',
    flexDirection                  : 'column',
    alignItems                     : 'center',
    width                          : '100%',
    [theme.breakpoints.down('sm')] : {
      padding : 0
    }
  },

  layout : {
    display             : 'grid',
    gridTemplateColumns : `repeat(auto-fit, minmax(${REPORT_CARD_WIDTH}, ${REPORT_CARD_WIDTH}))`,
    gap                 : '24px',
    margin              : '12px',
    width               : '100%',
    justifyContent      : toggleThemeStyle('center', 'start')
  },

  fullWidth : {
    gridColumn : '1 / -1'
  },

  topContainer : {
    display                        : 'flex',
    flexDirection                  : 'row',
    justifyContent                 : 'space-between',
    alignItems                     : 'center',
    [theme.breakpoints.down('sm')] : {
      flexDirection : 'column'
    }
  },

  search : {
    width : toggleThemeStyle('350px', '364px')
  },

  resultsCount : {
    width           : 'fit-content',
    height          : '24px',
    display         : 'inline-flex',
    padding         : '0 4px 0 8px',
    borderRadius    : '4px',
    backgroundColor : dlsColor('grey6')
  },

  resultsCountText : {
    color         : dlsColor('grey2'),
    fontSize      : '10px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '16px',
    letterSpacing : '1px'
  },

  endAdornment : {
    display              : 'flex',
    padding              : '0 10px',
    cursor               : 'pointer',
    '& .MuiSvgIcon-root' : {
      fontSize : 16
    }
  },

  noResultsText : {
    color         : dlsColor('grey1'),
    fontSize      : '24px',
    fontFamily    : 'Inter',
    fontWeight    : 500,
    lineHeight    : '20px',
    letterSpacing : '0'
  }
}));

const HomeView = ({
  toggleCategory,
  categories,
  allCategories,
  setSelected,
  setSideTab,
  categoryFilter,
  setCategoryFilter,
  selectedCategory,
  sortByDate = false,
  omitReportFilter = true
}: HomeViewProps) => {
  const classes      = useStyles();
  const descriptions = translate('sisense:analytics.descriptions', {returnObjects: true});

  const {
    isLegacy  : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const isSearch = !!searchValue?.value.trim().length;

  const currentDashboards = getCurrentDashboards({
    categoryFilter   : (isSearch || omitReportFilter) ? getReportFilterOptions(allCategories) as any : categoryFilter,
    searchValue      : searchValue.value,
    categories       : isSearch ? allCategories : categories,
    selectedCategory : (!isSearch && omitReportFilter) ? selectedCategory : null,
    sortByDate
  });

  const onSearchChange = useCallback((evt: ChangeEvent<{name?: string; value: string;}>) => {
    const {target: {value}} = evt;
    evt.persist();
    setSearchValue?.(value);
  }, []);

  const removeSearch = useCallback(() => {
    setSearchValue?.('');
  }, []);

  const searchFieldStyles = useCallback(() => ({
    searchFieldRoot : toggleThemeStyle({
      height          : '42px',
      border          : 'unset',
      borderBottom    : `1px solid ${dlsColor('grey3')}`,
      backgroundColor : 'transparent',
      margin          : '16px 0'
    }, {
      border          : `1px solid ${dlsColor('grey5')}`,
      height          : '44px',
      borderRadius    : '8px',
      backgroundColor : dlsColor('white')
    }),
    searchFieldInput : toggleThemeStyle({
      fontSize : '14px'
    }, {
      color         : dlsColor('grey3'),
      fontSize      : '14px',
      fontFamily    : 'Inter',
      fontWeight    : 400,
      lineHeight    : '20px',
      letterSpacing : '0'
    })
  }), []);

  return (
    <div className={classes.root}>
      <div className={classes.layout}>
        <div className={classes.fullWidth}>
          <div className={classes.topContainer}>
            <div className={classes.search}>
              <SearchField placeholder={translate(`sisense:analytics.search.${isCurrentTheme ? 'currentPlaceholder' : 'placeholder'}`)}
                overrides={searchFieldStyles}
                onChange={onSearchChange}
                optionsProps={{value: searchValue.value}}
                endAdornment={(
                  <div className={classes.endAdornment}
                    onClick={searchValue.value ? removeSearch : null}>
                    {searchValue.value ? isCurrentTheme
                      ? <CloseButton /> : <CloseIcon /> : isLegacyTheme
                      ? <TopNavSearchIcon /> : undefined}
                  </div>
                )}
                hideStartAdornment={isLegacyTheme} />
            </div>
            <Render.If condition={!omitReportFilter}>
              <div style={{width: '250px'}}>
                <ReportsFilter categories={categories}
                  categoryFilter={categoryFilter}
                  setCategoryFilter={setCategoryFilter} />
              </div>
            </Render.If>
          </div>
        </div>

        <div className={classes.fullWidth}>
          <div className={isCurrentTheme ? classes.resultsCount : ''}>
            <LabelTypography variantType='label1' className={isCurrentTheme ? classes.resultsCountText : ''}>
              {translate('sisense:analytics.results', {count: currentDashboards.length})}
            </LabelTypography>
          </div>
        </div>

        <Render>
          <Render.If condition={currentDashboards.length}>
            {currentDashboards.map(dashboard => {
              const description = descriptions[dashboard.oid];
              return (
                <Tooltip key={dashboard.oid}
                  arrow title={description}
                  renderOnlyChildren={!description}
                  placement='top'
                  width='196px' >
                  <div>
                    <ReportCard dashboard={dashboard}
                      setSideTab={setSideTab}
                      setSelected={setSelected}
                      toggleCategory={toggleCategory}
                      description={description} />
                  </div>
                </Tooltip>
              );
            })}
          </Render.If>
          <Render.Else>
            <div className={classes.fullWidth}>
              <Render.If condition={isLegacyTheme}>
                <h2>{translate('sisense:analytics.noResults')}</h2>
              </Render.If>
              <Render.If condition={isCurrentTheme}>
                <span className={classes.noResultsText}>
                  {translate('sisense:analytics.noResults')}
                </span>
              </Render.If>
            </div>
          </Render.Else>
        </Render>
      </div>
    </div>
  );
};

export default withSignals(HomeView);
